import { type CuisineListItem, type CuisineListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { CuisineFormDrawer } from '../components/CuisineFormDrawer';
import { listCuisines } from '../cuisines.api';
import {
  useBulkDeleteCuisines,
  useBulkSetCuisinesStatus,
  useCuisinesList,
  useDeleteCuisine,
} from '../use-cuisines';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const CuisinesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCuisine = useDeleteCuisine();
  const bulkDeleteMut = useBulkDeleteCuisines();
  const bulkSetStatusMut = useBulkSetCuisinesStatus();

  const columns: ListScreenColumns<CuisineListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CuisineListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'region',
        header: 'Region',
        cell: (row) =>
          row.region ? <UzBadge tone="secondary">{row.region}</UzBadge> : <span>-</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CuisineListItem, CuisineListQuery>
      title="Cuisines"
      description="Cuisine taxonomy. Powers the MealService picker filter."
      useList={useCuisinesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add cuisine' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CuisineFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          cuisine={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCuisine.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this cuisine?',
            description: `${row.name} will be soft-deleted.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} cuisine${rows.length === 1 ? '' : 's'}?`,
            description: 'These cuisines will be soft-deleted.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} cuisine${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in meal service pickers.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCuisines(q),
        filename: 'cuisines',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Region', value: (r) => r.region ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by code, name, or region',
        emptyTitle: 'No cuisines match these filters',
        emptyDescription: 'Adjust the filter or clear the search box.',
        errorTitle: 'Failed to load cuisines',
        errorDescription: 'Try refreshing.',
      }}
    />
  );
};
